import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ActionIcon, Button, Group, Menu, Modal, Skeleton, Stack, Text, ThemeIcon } from "@mantine/core";
import { useMediaQuery, useWindowEvent } from "@mantine/hooks";
import * as Sentry from "@sentry/react";
import { BookOpen, EllipsisVertical, RotateCcw, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { GuideProgress } from "@nodus/ui";
import { GUIDES_KEY, guideQueryKey, guidesApi, type GuideDetail, type GuideStateAction, type GuideStep } from "@/lib/api/guides";
import {
  arriveAt,
  blockCompletedHandler,
  keepOpen,
  displayStatus,
  escapeClosesGuide,
  hideStep,
  NO_VISIT,
  opensFinished,
  practiceTarget,
  revealStep,
  stepperActions,
  type PracticeNotice,
  type StepVisit,
  GUIDE_OPENED_STATE,
  guideKeyFromSearch,
  openedByUs,
  initialScreenIndex,
  rememberedScreen,
  rememberScreen,
  stepperScreens,
  stepperSteps,
  withGuide,
} from "@/lib/guide-stepper";
import { GuideModalContext, type GuideModalApi } from "@/lib/guide-modal-context";
import { faqIcon } from "@/lib/faq-icons";
import { MY_GUIDES_KEY, useGuidesRealtime, useMyGuides } from "@/hooks/use-guides";
import { useGuideModal } from "@/hooks/use-guide-modal";
import { useScreenTaken } from "@/hooks/use-screen-taken";
import { hasBlock } from "./blocks/registry";
import { practiceNotice } from "./blocks/practice";
import { GuideStepperView } from "./guide-stepper-view";

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null)?.response?.status;
}

/**
 * Renders the guide modal (#436) — a guide is always a popup, never a page.
 * Which guide is open lives in the URL: `?guide=<key>` on whatever page the
 * member is on (`/?guide=g%3A12` from outside the portal opens it on the
 * dashboard). Opening adds the parameter as a history entry (marked with
 * `GUIDE_OPENED_STATE`), so Back closes the guide and Forward reopens it.
 * Closing goes back over that entry — no duplicate of the page is left, so
 * the next Back really leaves it; a guide that arrived with the page (a
 * `/?guide=` link) is closed with `replace`. A link inside a step goes to a
 * page without `?guide=`, which closes the guide by itself.
 */
export function GuideModalProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { pathname, search, hash, state } = useLocation();
  const openKey = guideKeyFromSearch(search);
  const openGuide = useCallback(
    (key: string) => navigate({ pathname, search: withGuide(search, key), hash }, { state: GUIDE_OPENED_STATE }),
    [navigate, pathname, search, hash],
  );
  const closeGuide = useCallback(() => {
    if (openedByUs(state)) navigate(-1);
    else navigate({ pathname, search: withGuide(search, null), hash }, { replace: true });
  }, [navigate, pathname, search, hash, state]);
  const value = useMemo<GuideModalApi>(() => ({ openKey, openGuide, closeGuide }), [openKey, openGuide, closeGuide]);
  return (
    <GuideModalContext.Provider value={value}>
      {children}
      <GuideModal />
    </GuideModalContext.Provider>
  );
}

/**
 * The guide modal: one screen at a time (see `lib/guide-stepper.ts`). Full
 * screen on a phone, with Back/Next pinned to the bottom. It steps aside while
 * a fullscreen announcement, a takeover or an emergency is showing.
 */
function GuideModal() {
  const modal = useGuideModal();
  const key = modal?.openKey ?? null;
  const mobile = useMediaQuery("(max-width: 48em)");
  const { covered } = useScreenTaken();
  if (!modal) return null;

  return (
    // Esc is handled by the stepper itself: Mantine closes every open modal on
    // Esc, so a dialog a block opens (the room finder) would take the guide with it.
    <Modal.Root
      opened={!!key && !covered}
      onClose={modal.closeGuide}
      closeOnEscape={false}
      fullScreen={!!mobile}
      size="lg"
      centered
    >
      <Modal.Overlay />
      {key && <GuideStepper key={key} guideKey={key} onClose={modal.closeGuide} fullScreen={!!mobile} />}
    </Modal.Root>
  );
}

function GuideStepper({ guideKey, onClose, fullScreen }: { guideKey: string; onClose: () => void; fullScreen: boolean }) {
  const qc = useQueryClient();
  useGuidesRealtime();
  const { data: mine } = useMyGuides();

  const { data: guide, isLoading, error } = useQuery({
    queryKey: guideQueryKey(guideKey),
    queryFn: () => guidesApi.get(guideKey).then((r) => r.data),
    retry: (count, err) => (httpStatus(err) === 404 ? false : count < 2),
  });

  const apply = (detail: GuideDetail) => {
    qc.setQueryData(guideQueryKey(guideKey), detail);
    qc.invalidateQueries({ queryKey: MY_GUIDES_KEY });
  };

  const mark = useMutation({
    mutationFn: ({ stepKey, done, company }: { stepKey: string; done: boolean; company: boolean }) =>
      guidesApi.markStep(guideKey, stepKey, done, company).then((r) => r.data),
    onSuccess: apply,
    onError: (err) => {
      if (httpStatus(err) === 409) toast.info("This step is checked for you when it is done.");
      else toast.error("Could not save this step. Please try again.");
      qc.invalidateQueries({ queryKey: GUIDES_KEY });
    },
  });

  const state = useMutation({
    mutationFn: (action: GuideStateAction) => guidesApi.setState(guideKey, action).then((r) => r.data),
    onSuccess: apply,
    onError: (err, action) => {
      // "seen" is bookkeeping: no toast, but not silent either.
      if (action === "seen") Sentry.captureException(err, { tags: { kind: "guide-seen" } });
      else toast.error("Could not save. Please try again.");
    },
  });

  // First open: record that the member has seen the guide ("in progress").
  const seen = useRef(false);
  const setState = state.mutate;
  useEffect(() => {
    if (guide?.status === "not_started" && !seen.current) {
      seen.current = true;
      setState("seen");
    }
  }, [guide?.status, setState]);

  // Members never see company steps (the API leaves them out); until the
  // list says otherwise, trust the API.
  const isCompanyAdmin = mine ? mine.is_company_admin : true;
  const pending = mark.isPending ? mark.variables : undefined;
  const steps = useMemo(() => {
    if (!guide) return [];
    const visible = stepperSteps(guide.steps, hasBlock, isCompanyAdmin);
    // Show a tick that is being saved straight away.
    return pending ? visible.map((s) => (s.key === pending.stepKey ? { ...s, done: pending.done } : s)) : visible;
  }, [guide, isCompanyAdmin, pending]);
  const screens = useMemo(() => (guide ? stepperScreens(guide.intro, steps) : []), [guide, steps]);

  // Where the member is, by screen id — a refetch that adds or removes steps
  // doesn't move them. Picked once, when the guide first loads.
  // A finished guide opens on its completion screen.
  const [atId, setAtId] = useState<string | null>(null);
  // Done steps whose form shows anyway, while the member is on them (#436 done
  // states, see `StepVisit`). Starts over on every screen change.
  const [visit, setVisit] = useState<StepVisit>(NO_VISIT);
  // "This was a practice run", on the screen a practice run moved to. Gone
  // once the member moves again.
  const [practiced, setPracticed] = useState<PracticeNotice | null>(null);
  const arrive = (target: (typeof screens)[number]) => {
    setAtId(target.id);
    setVisit(arriveAt(target));
    setPracticed(null);
  };
  if (guide && atId === null && screens.length > 0) {
    arrive(screens[initialScreenIndex(screens, rememberedScreen(guideKey), opensFinished(guide.status, steps))]);
  }
  const found = screens.findIndex((s) => s.id === atId);
  const index = found >= 0 ? found : Math.max(0, initialScreenIndex(screens, null));

  const go = (i: number) => {
    const target = screens[i];
    if (!target) return;
    arrive(target);
    rememberScreen(guideKey, target.id);
  };
  const actions = stepperActions(screens, index, {
    mark: (tick) => mark.mutate({ ...tick, done: true }),
    go,
    close: onClose,
  });

  // Esc closes the guide only when it is the topmost dialog (see escapeClosesGuide).
  const contentRef = useRef<HTMLDivElement | null>(null);
  useWindowEvent("keydown", (e) => {
    if (e.key !== "Escape" || e.defaultPrevented) return;
    const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]').length;
    if (escapeClosesGuide(contentRef.current, document.activeElement, document.body, dialogs)) onClose();
  });

  const onBlockCompleted = (step: GuideStep) =>
    blockCompletedHandler(step, {
      keepOpen: (key) => setVisit((v) => keepOpen(v, key)),
      tick: (tick) => mark.mutate({ ...tick, done: true }),
      refetch: () => void qc.invalidateQueries({ queryKey: GUIDES_KEY }),
      // A practice run (no API call was made): on to the next screen — the
      // end screen after the last step — saying it was practice.
      practiced: (stepKey) => {
        const to = practiceTarget(screens, stepKey);
        if (to < 0) return;
        go(to);
        setPracticed({ at: screens[to].id, text: practiceNotice(step.block_key) });
      },
    });

  const skip = () =>
    state.mutate("dismiss", {
      onSuccess: () => {
        toast.success("Guide skipped. You can find it again in the Office Guide.");
        onClose();
      },
    });

  const Icon = guide?.icon ? faqIcon(guide.icon) : BookOpen;
  // Full width on a phone even when the screen's content is short (a done step).
  const contentStyles = fullScreen ? { display: "flex", flexDirection: "column" as const, width: "100%" } : undefined;

  return (
    <Modal.Content ref={contentRef} style={contentStyles}>
      <Modal.Header style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
        <Group gap="sm" wrap="nowrap" flex={1} miw={0} mr="xs">
          <ThemeIcon variant="light" size={36} radius="md" style={{ flexShrink: 0 }}>
            <Icon size={20} />
          </ThemeIcon>
          <Stack gap={4} miw={0} flex={1}>
            <Modal.Title fw={700} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {guide?.title ?? "Guide"}
            </Modal.Title>
            {guide && <GuideProgress done={guide.done} total={guide.total} status={displayStatus(guide.status, steps)} />}
          </Stack>
        </Group>
        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
          {guide && guide.status !== "done" && (
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Guide options">
                  <EllipsisVertical size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {guide.status === "dismissed" ? (
                  <Menu.Item leftSection={<RotateCcw size={16} />} onClick={() => setState("reopen")}>
                    Show this guide again
                  </Menu.Item>
                ) : (
                  <Menu.Item leftSection={<SkipForward size={16} />} onClick={skip}>
                    Skip this guide
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
          <Modal.CloseButton aria-label="Close guide" />
        </Group>
      </Modal.Header>
      <Modal.Body
        pt="md"
        pb={0}
        style={fullScreen ? { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 } : undefined}
      >
        {isLoading ? (
          <Stack gap="md" pb="md">
            <Skeleton height={28} width="60%" />
            <Skeleton height={120} />
          </Stack>
        ) : error || !guide ? (
          <Stack gap="md" pb="md">
            <Text c="dimmed">
              {httpStatus(error) === 404 || !error
                ? "This guide is not available."
                : "Could not load this guide. Please try again later."}
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                Close
              </Button>
            </Group>
          </Stack>
        ) : (
          <GuideStepperView
            guide={guide}
            steps={steps}
            screens={screens}
            index={index}
            onGo={go}
            onNext={actions.next}
            onBack={actions.back}
            onGoThroughAgain={actions.goThroughAgain}
            onClose={onClose}
            onUntick={(s) => mark.mutate({ stepKey: s.key, done: false, company: s.audience === "admins" })}
            onBlockCompleted={onBlockCompleted}
            pendingKey={pending?.stepKey ?? null}
            visit={visit}
            onReveal={(s) => setVisit((v) => revealStep(v, s.key))}
            onHide={(s) => setVisit((v) => hideStep(v, s.key))}
            practiced={practiced}
          />
        )}
      </Modal.Body>
    </Modal.Content>
  );
}
